import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import Select from "react-select";
import { useMiCuestionario } from './hooks/useMiCuestionario';
import CustomDatePicker from '../../components/users/steps/CustomDatePicker';

const MiCuestionarioTab = () => {
    const { getMiCuestionario, responderBulk, completar } = useMiCuestionario();
    const [cuestionarios, setCuestionarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [respuestas, setRespuestas] = useState({});
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const fetchCuestionarios = async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const res = await getMiCuestionario();
            // res.data is now an array
            setCuestionarios(res.data);
            
            // Inicializar respuestas locales para TODOS los cuestionarios
            const initResp = {};
            res.data.forEach(c => {
                if (c.preguntas) {
                    c.preguntas.forEach(p => {
                        if (p.respuesta) {
                            if (p.tipo_pregunta === "TEXTO" || p.tipo_pregunta === "NUMERICO") {
                                initResp[p.id] = { respuesta_texto: p.respuesta.respuesta_texto || "" };
                            } else if (p.tipo_pregunta === "FECHA") {
                                initResp[p.id] = { respuesta_fecha: p.respuesta.respuesta_fecha || "" };
                            } else if (p.tipo_pregunta === "BOOLEANO") {
                                initResp[p.id] = { respuesta_booleano: p.respuesta.respuesta_booleano };
                            } else if (p.tipo_pregunta === "SELECCION") {
                                initResp[p.id] = { opciones_ids: p.respuesta.opciones_ids || [] };
                            }
                        } else {
                            if (p.tipo_pregunta === "TEXTO" || p.tipo_pregunta === "NUMERICO") {
                                initResp[p.id] = { respuesta_texto: "" };
                            } else if (p.tipo_pregunta === "FECHA") {
                                initResp[p.id] = { respuesta_fecha: "" };
                            } else if (p.tipo_pregunta === "BOOLEANO") {
                                initResp[p.id] = { respuesta_booleano: null };
                            } else if (p.tipo_pregunta === "SELECCION") {
                                initResp[p.id] = { opciones_ids: [] };
                            }
                        }
                    });
                }
            });
            setRespuestas(initResp);
        } catch (error) {
            setErrorMsg("Error al cargar los cuestionarios.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCuestionarios();
    }, []);

    const handleChange = (preguntaId, tipo, multi, value) => {
        setRespuestas(prev => {
            const current = prev[preguntaId] || {};
            let updated = { ...current };

            if (tipo === "TEXTO" || tipo === "NUMERICO") {
                updated.respuesta_texto = value;
            } else if (tipo === "FECHA") {
                updated.respuesta_fecha = value;
            } else if (tipo === "BOOLEANO") {
                updated.respuesta_booleano = value === 'true';
            } else if (tipo === "SELECCION") {
                if (multi) {
                    if (Array.isArray(value)) {
                        updated.opciones_ids = value;
                    } else {
                        const currentIds = updated.opciones_ids || [];
                        if (currentIds.includes(value)) {
                            updated.opciones_ids = currentIds.filter(id => id !== value);
                        } else {
                            updated.opciones_ids = [...currentIds, value];
                        }
                    }
                } else {
                    updated.opciones_ids = [value];
                }
            }

            return { ...prev, [preguntaId]: updated };
        });
    };

    const handleSaveAll = async (cuestionarioId) => {
        setErrorMsg("");
        setSuccessMsg("");
        
        // Find questions belonging to this cuestionario
        const cuestionario = cuestionarios.find(c => c.id === cuestionarioId);
        if (!cuestionario) return;
        
        const questionIds = cuestionario.preguntas?.map(p => p.id) || [];
        
        const payload = [];
        for (const [preguntaIdStr, data] of Object.entries(respuestas)) {
            const preguntaId = parseInt(preguntaIdStr);
            if (!questionIds.includes(preguntaId)) continue;
            
            const item = { pregunta: preguntaId };
            if (data.respuesta_texto !== undefined && data.respuesta_texto !== "") item.respuesta_texto = data.respuesta_texto;
            if (data.respuesta_fecha !== undefined && data.respuesta_fecha !== "") item.respuesta_fecha = data.respuesta_fecha;
            if (data.respuesta_booleano !== undefined && data.respuesta_booleano !== null) item.respuesta_booleano = data.respuesta_booleano;
            if (data.opciones_ids !== undefined && data.opciones_ids.length > 0) item.opciones_ids = data.opciones_ids;
            
            if (Object.keys(item).length > 1) {
                payload.push(item);
            }
        }

        try {
            await responderBulk({ respuestas: payload });
            setSuccessMsg("Respuestas guardadas exitosamente.");
            fetchCuestionarios();
        } catch (error) {
            setErrorMsg("Error al guardar respuestas.");
        }
    };

    const handleComplete = async (cuestionarioId) => {
        setErrorMsg("");
        setSuccessMsg("");
        try {
            await handleSaveAll(cuestionarioId);
            await completar(cuestionarioId);
            setSuccessMsg("¡Cuestionario completado exitosamente!");
            fetchCuestionarios();
        } catch (error) {
            if (error.response?.data?.preguntas_faltantes) {
                setErrorMsg(`Faltan preguntas obligatorias: ${error.response.data.preguntas_faltantes.join(', ')}`);
            } else {
                setErrorMsg("Error al completar el cuestionario. Asegúrate de haber respondido todas las preguntas obligatorias.");
            }
        }
    };

    if (loading) {
        return <div className="text-center py-5">Cargando cuestionarios...</div>;
    }

    if (!cuestionarios || cuestionarios.length === 0) {
        return (
            <div className="text-center py-5">
                <h5 className="text-secondary-light">No hay cuestionarios disponibles por el momento.</h5>
            </div>
        );
    }

    return (
        <div>
            {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}
            {successMsg && <div className="alert alert-success">{successMsg}</div>}

            <ul className="nav nav-pills mb-24 d-inline-flex border-gradient-tab gap-2" id="cuestionarios-tab" role="tablist">
                {cuestionarios.map((cuestionario, idx) => (
                    <li className="nav-item" role="presentation" key={`nav-${cuestionario.id}`}>
                        <button
                            className={`nav-link d-flex align-items-center px-24 ${idx === 0 ? 'active' : ''}`}
                            id={`cuestionario-${cuestionario.id}-tab`}
                            data-bs-toggle="pill"
                            data-bs-target={`#cuestionario-${cuestionario.id}`}
                            type="button"
                            role="tab"
                            aria-controls={`cuestionario-${cuestionario.id}`}
                            aria-selected={idx === 0}
                        >
                            {cuestionario.nombre}
                            {cuestionario.completado ? (
                                <Icon icon="mdi:check-circle" className="text-success ms-2 fs-5" />
                            ) : (
                                <Icon icon="mdi:clock-outline" className="text-warning ms-2 fs-5" />
                            )}
                        </button>
                    </li>
                ))}
            </ul>

            <div className="tab-content" id="cuestionarios-tabContent">
                {cuestionarios.map((cuestionario, idx) => {
                    const progressPercent = cuestionario.total_preguntas 
                        ? (cuestionario.preguntas_respondidas / cuestionario.total_preguntas) * 100 
                        : 0;

                    return (
                        <div
                            className={`tab-pane fade ${idx === 0 ? 'show active' : ''}`}
                            id={`cuestionario-${cuestionario.id}`}
                            role="tabpanel"
                            aria-labelledby={`cuestionario-${cuestionario.id}-tab`}
                            tabIndex={0}
                            key={cuestionario.id}
                        >
                            <div className="mb-24">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h5 className="mb-0">{cuestionario.nombre}</h5>
                                    {cuestionario.completado ? (
                                        <span className="badge bg-success px-12 py-6">Completado</span>
                                    ) : (
                                        <span className="badge bg-warning px-12 py-6">Pendiente</span>
                                    )}
                                </div>
                                <p className="text-secondary-light mb-16">{cuestionario.descripcion}</p>
                                
                                <div className="progress" style={{ height: "8px" }}>
                                    <div 
                                        className={`progress-bar ${cuestionario.completado ? 'bg-success' : 'bg-primary'}`}
                                        role="progressbar" 
                                        style={{ width: `${progressPercent}%` }} 
                                        aria-valuenow={cuestionario.preguntas_respondidas} 
                                        aria-valuemin="0" 
                                        aria-valuemax={cuestionario.total_preguntas}
                                    ></div>
                                </div>
                                <div className="text-end text-sm text-secondary-light mt-1">
                                    {cuestionario.preguntas_respondidas} de {cuestionario.total_preguntas} respondidas
                                </div>
                            </div>
                            
                            <div className="questions-list">
                                {cuestionario.preguntas?.map((pregunta, index) => {
                                    const ans = respuestas[pregunta.id] || {};
                                    return (
                                        <div key={pregunta.id} className="card border mb-24 shadow-none radius-8 bg-neutral-50">
                                            <div className="card-body p-20">
                                                <h6 className="text-md mb-16">
                                                    {index + 1}. {pregunta.pregunta}
                                                    {pregunta.obligatorio && <span className="text-danger-600 ms-1">*</span>}
                                                </h6>
                                                
                                                <div className="mt-16">
                                                    {pregunta.tipo_pregunta === "TEXTO" && (
                                                        <textarea
                                                            className="form-control radius-8"
                                                            rows="3"
                                                            value={ans.respuesta_texto || ""}
                                                            onChange={(e) => handleChange(pregunta.id, "TEXTO", false, e.target.value)}
                                                            
                                                            placeholder="Escribe tu respuesta aquí..."
                                                        />
                                                    )}

                                                    {pregunta.tipo_pregunta === "NUMERICO" && (
                                                        <input
                                                            type="number"
                                                            className="form-control radius-8"
                                                            value={ans.respuesta_texto || ""}
                                                            onChange={(e) => handleChange(pregunta.id, "NUMERICO", false, e.target.value)}
                                                            
                                                            placeholder="Ingresa un número"
                                                        />
                                                    )}

                                                    {pregunta.tipo_pregunta === "FECHA" && (
                                                        <CustomDatePicker
                                                            name={`fecha-${pregunta.id}`}
                                                            value={ans.respuesta_fecha || ""}
                                                            onChange={(name, val) => handleChange(pregunta.id, "FECHA", false, val)}
                                                            label="Selecciona una fecha"
                                                        />
                                                    )}

                                                    {pregunta.tipo_pregunta === "BOOLEANO" && (
                                                        <div className="d-flex gap-4">
                                                            <div className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="radio"
                                                                    name={`bool-${pregunta.id}`}
                                                                    id={`bool-${pregunta.id}-true`}
                                                                    checked={ans.respuesta_booleano === true}
                                                                    onChange={() => handleChange(pregunta.id, "BOOLEANO", false, 'true')}
                                                                    
                                                                />
                                                                <label className="form-check-label" htmlFor={`bool-${pregunta.id}-true`}>
                                                                    Sí
                                                                </label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="radio"
                                                                    name={`bool-${pregunta.id}`}
                                                                    id={`bool-${pregunta.id}-false`}
                                                                    checked={ans.respuesta_booleano === false}
                                                                    onChange={() => handleChange(pregunta.id, "BOOLEANO", false, 'false')}
                                                                    
                                                                />
                                                                <label className="form-check-label" htmlFor={`bool-${pregunta.id}-false`}>
                                                                    No
                                                                </label>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {pregunta.tipo_pregunta === "SELECCION" && (
                                                        pregunta.multi_respuesta ? (
                                                            <Select
                                                                isMulti
                                                                options={pregunta.opciones?.map(o => ({ value: o.id, label: o.opcion_texto }))}
                                                                value={pregunta.opciones?.filter(o => ans.opciones_ids?.includes(o.id)).map(o => ({ value: o.id, label: o.opcion_texto })) || []}
                                                                onChange={(selectedOptions) => {
                                                                    const selectedIds = selectedOptions ? selectedOptions.map(o => o.value) : [];
                                                                    handleChange(pregunta.id, "SELECCION", true, selectedIds);
                                                                }}
                                                                placeholder="Selecciona las opciones..."
                                                                className="react-select-container"
                                                                classNamePrefix="react-select"
                                                            />
                                                        ) : (
                                                            <select
                                                                className="form-control radius-8 form-select"
                                                                value={ans.opciones_ids?.[0] || ""}
                                                                onChange={(e) => handleChange(pregunta.id, "SELECCION", false, parseInt(e.target.value))}
                                                            >
                                                                <option value="" disabled>Selecciona una opción</option>
                                                                {pregunta.opciones?.map(opcion => (
                                                                    <option key={opcion.id} value={opcion.id}>
                                                                        {opcion.opcion_texto}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="d-flex align-items-center justify-content-end gap-3 mt-16">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary border border-primary-600 text-primary-600 text-md px-32 py-12 radius-8 bg-hover-primary-50"
                                    onClick={() => handleSaveAll(cuestionario.id)}
                                >
                                    {cuestionario.completado ? "Actualizar Respuestas" : "Guardar Progreso"}
                                </button>
                                {!cuestionario.completado && (
                                    <button
                                        type="button"
                                        className="btn btn-primary border border-primary-600 text-md px-32 py-12 radius-8"
                                        onClick={() => handleComplete(cuestionario.id)}
                                    >
                                        Completar Cuestionario
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MiCuestionarioTab;
